import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { FormattedMessage, useIntl } from "react-intl";
import { Grid, PageWrapper } from "components/Containers";
import PageTitle from "components/PageTitle";
import { TimeCardFilterMenu, TimeCardList } from "components/TimeCard";
import Card from "components/Card";
import { formatDate, formatNumber, getApiLimitSuffix, isEmpty } from "utils";
import { useParamFunctions } from "hooks";

/* defaults */
const DATE = formatDate(new Date(), "YYYY-MM-DD");

const KEYS = {
  filters: "f",
  advancedFilters: "af",
  period: "period",
  search: "search",
  organizationIds: "organizationIds",
  teamIds: "teamIds",
  partnerIds: "partnerIds",
  statuses: "statuses",
  overAllocatedHours: "overAllocatedHours",
  hasOnboardOffboardDates: "hasOnboardOffboardDates",
};

/**
 * TimeCard Page
 */
const TimeCard = () => {
  const intl = useIntl();
  const { paramParse, setParams } = useParamFunctions();
  const prevValuesRef = useRef();

  // Parse URL parameters
  const initialFilters = paramParse(KEYS.filters);
  const initialAdvancedFilters = paramParse(KEYS.advancedFilters);

  const [totalCount, setTotalCount] = useState(0);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [data, setData] = useState();

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialFilters?.[KEYS.search] ?? null);

  const [values, setValues] = useState({
    year: formatDate(initialFilters?.[KEYS.period] ?? DATE, "YYYY"),
    month: formatDate(initialFilters?.[KEYS.period] ?? DATE, "MM"),
    period: initialFilters?.[KEYS.period] ?? DATE,
    organizationIds: initialAdvancedFilters?.[KEYS.organizationIds] ?? null,
    teamIds: initialAdvancedFilters?.[KEYS.teamIds] ?? null,
    partnerIds: initialAdvancedFilters?.[KEYS.partnerIds] ?? null,
    overAllocatedHours: initialAdvancedFilters?.[KEYS.overAllocatedHours] ?? null,
    hasOnboardOffboardDates: initialAdvancedFilters?.[KEYS.hasOnboardOffboardDates] ?? null,
    statuses: initialAdvancedFilters?.[KEYS.statuses] ?? null,
    search: initialFilters?.[KEYS.search] ?? null,
  });

  const handleChange = (val) => {
    if (val.search !== values.search) {
      setSearch(val.search);
    } else {
      setValues({
        ...val,
        teamIds: val.teamIds?.length ? val.teamIds : null,
        organizationIds: val.organizationIds?.length ? val.organizationIds : null,
        partnerIds: val.partnerIds?.length ? val.partnerIds : null,
        statuses: val.statuses?.length ? val.statuses : null,
        year: formatDate(val.period, "YYYY", false, true),
        month: formatDate(val.period, "MM", false, true),
        period: formatDate(val.period, "YYYY-MM-DD", false, true),
      });
    }
  };

  const handleSubmit = () => {
    setValues({
      ...values,
      search: search,
    });
  };

  /**
   * @description Update URL params when filter values change
   */
  useEffect(() => {
    // Skip if values haven't changed
    if (JSON.stringify(prevValuesRef.current) === JSON.stringify(values)) return;

    // Set basic filters (period and search)
    setParams(
      KEYS.filters,
      {
        [KEYS.period]: values.period,
        [KEYS.search]: values.search,
      },
      true,
      true
    );

    // Set advanced filters
    setParams(
      KEYS.advancedFilters,
      {
        [KEYS.organizationIds]: values.organizationIds,
        [KEYS.teamIds]: values.teamIds,
        [KEYS.partnerIds]: values.partnerIds,
        [KEYS.statuses]: values.statuses,
        [KEYS.overAllocatedHours]: values.overAllocatedHours,
        [KEYS.hasOnboardOffboardDates]: values.hasOnboardOffboardDates,
      },
      true,
      true
    );

    // Update the ref with current values
    prevValuesRef.current = values;
  }, [values, setParams]);

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.TimeCard.Title" })}</title>
      </Helmet>
      <Grid>
        <Grid.col start={1} end={13}>
          <PageTitle
            title={<FormattedMessage id={`TimeCard.H1`} />}
            loading={loading}
            subtitle={
              <FormattedMessage
                id="TimeCard.Title"
                values={{
                  submittedCount: <b>{submittedCount}</b>,
                  members: (
                    <b>
                      <FormattedMessage
                        id="TimeCard.Count"
                        values={{
                          totalCount,
                          formattedNumber: formatNumber(totalCount, getApiLimitSuffix(totalCount)),
                        }}
                      />
                    </b>
                  ),
                }}
              />
            }
          />
        </Grid.col>
        <Grid.col start={1} end={13}>
          <Card>
            <TimeCardFilterMenu
              initialValues={values}
              formValues={data?.timeCardReportForMonth?.filterMetadata}
              onChange={handleChange}
              onSubmit={handleSubmit}
              disabled={loading}
              initialShowFilters={!isEmpty(initialAdvancedFilters)}
              hideOptionalLabel
            />
          </Card>
        </Grid.col>
        <Grid.col start={1} end={13}>
          <Card>
            <TimeCardList
              filters={values}
              selectedDate={values.period}
              setTotalCount={setTotalCount}
              setSubmittedCount={setSubmittedCount}
              onDataFetched={setData}
              onLoading={setLoading}
            />
          </Card>
        </Grid.col>
      </Grid>
    </PageWrapper>
  );
};

export default TimeCard;
